import uploadIcon from "@assets/images/upload.svg";
import warningIcon from "@assets/images/warning-red.svg";
import { Box, Button } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { FormikProps } from "formik";
import React from "react";
import { useDropzone } from "react-dropzone";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import {
  DocumentType,
  ICashsaleFormValues,
  IDocumentFile,
  PaymentMethod
} from "../types";
import { ACCEPTED_FILE_EXTENSION } from "../useCashSaleForm";
import DocumentList from "./DocumentList";
import useStyle from "./styles";

export interface DocumentUploaderProps {
  formik: FormikProps<ICashsaleFormValues>;
  isEditable?: boolean;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  formik,
  isEditable = false
}) => {
  const { values, errors, setFieldValue } = formik;

  const documentFiles = values?.documentFiles ?? [];
  const documentFilesErrors = errors.documentFiles;
  const documentFilesSummaryErrors = (errors as any).documentFilesSummary as
    | string[]
    | undefined;

  const classes = useStyle();

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: ACCEPTED_FILE_EXTENSION,
    onDrop: (acceptedFiles, fileRejections) => {
      let result: File | undefined;
      if (acceptedFiles.length > 0) {
        result = acceptedFiles[0];
      } else if (fileRejections.length > 0) {
        result = fileRejections[0].file;
      }
      if (!result) {
        return;
      }

      const initialDocumentValue: IDocumentFile = {
        file: result,
        documentType: DocumentType.PAYIN,
        paymentMethod: PaymentMethod.BANK_TRANSFER
      };

      setFieldValue("documentFiles", [...documentFiles, initialDocumentValue]);
    }
  });

  const onRemoveItem = (index: number) => {
    const newFiles = documentFiles.filter((_, i) => i !== index);
    setFieldValue("documentFiles", newFiles);
  };

  const onChangeItemDocumentType = (
    index: number,
    documentType: DocumentType
  ) => {
    const paymentMethod: PaymentMethod =
      documentType === DocumentType.PAYIN ? PaymentMethod.BANK_TRANSFER : null;
    setFieldValue(`documentFiles[${index}]`, {
      ...values.documentFiles[index],
      documentType,
      paymentMethod
    });
  };

  const onChangeItemPaymentMethod = (
    index: number,
    paymentMethod: PaymentMethod
  ) => {
    setFieldValue(`documentFiles[${index}].paymentMethod`, paymentMethod);
  };

  const body = (
    <Box display="flex" flexDirection="column" flex={1} gridGap={16}>
      {isEditable && (
        <Box display="flex" alignItems="center" gridGap={16}>
          <div>
            <Button
              id="document-upload-button"
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<InlineSVG src={uploadIcon} width={18} />}
              {...getRootProps({ className: classes.uploadButton })}
            >
              <MyTypography variant="subtitle3">
                <FormattedMessage defaultMessage="Upload File" />
              </MyTypography>
            </Button>
            <input {...getInputProps()} data-testid="document-upload-input" />
          </div>
          <MyTypography variant="body3">
            <FormattedMessage defaultMessage="กรุณาอัปโหลด" />{" "}
            <strong>
              <FormattedMessage defaultMessage="Pay-in Slip" />
            </strong>{" "}
            <FormattedMessage defaultMessage="หรือเอกสารอื่น ๆ เพื่อดำเนินการคำสั่งซื้อ" />
          </MyTypography>
        </Box>
      )}

      {documentFilesSummaryErrors?.length > 0 && (
        <Box display="flex" flexDirection="column" gridGap={10}>
          {documentFilesSummaryErrors.map(message => (
            <MyTypography
              key={message}
              variant="body3"
              className={classes.errorAlert}
            >
              <InlineSVG
                src={warningIcon}
                width={20}
                className={classes.errorAlertIcon}
              />
              <span dangerouslySetInnerHTML={{ __html: message }} />
            </MyTypography>
          ))}
        </Box>
      )}

      {documentFiles.length > 0 && (
        <DocumentList
          items={documentFiles}
          errors={documentFilesErrors}
          isEditable={isEditable}
          onRemove={onRemoveItem}
          onChangeDocumentType={onChangeItemDocumentType}
          onChangePaymentMethod={onChangeItemPaymentMethod}
        />
      )}
    </Box>
  );

  return (
    <Box
      className={classes.root}
      data-testid="excel-upload-file"
      display={"flex"}
      flexDirection={"column"}
    >
      {body}
    </Box>
  );
};

export default DocumentUploader;
